import { v4 as uuidv4 } from 'uuid';
import { useEffect, useRef, useState } from 'react';
import useMediaRecorder from './audio/use-media-recorder';
import { ITranscriptionClient } from '../api-service/clients';
import { blobToBase64 } from './audio/blob-to-base64';

export enum TranscriptionStatus {
  Inactive = 'inactive',
  Active = 'active',
  Paused = 'paused',
  Processing = 'processing',
  Failed = 'processing-failed',
  ProcessingTimedOut = 'processing-timed-out',
  Completed = 'completed',
}

export type IUseTranscription = {
  startPollingForTranscription: () => Promise<void>;
  transcriptionStatus: TranscriptionStatus;
  errorMessage: string;
  startRecording: (includeSystemAudio: boolean, audioInputDeviceId: string | undefined) => void;
  stopRecording: () => void;
  micInputAnalyser: AnalyserNode | null;
  systemAudioAnalyser: AnalyserNode | null;
  recordingStartTime: number | null; // Unix timestamp (ms) when recording started
  abortRecording: () => void;
  sessionId: string;
};

function useTranscription(transcriptionClient: ITranscriptionClient, options: { pollingTimeoutMs?: number; audioChunkSizeSeconds?: number }): IUseTranscription {
  const sessionId = useRef<string>(uuidv4());
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingStartTimeRef = useRef<number | null>(null);
  const transcriptionUploadQueue = useRef<Map<number, PartialTranscriptionUploadState>>(new Map());
  const nextOrder = useRef<number>(0);
  const maxUploadRetries = 3;
  const { pollingTimeoutMs = 60 * 2 * 1000, audioChunkSizeSeconds = 5 * 60 } = options;
  const [transcriptionStatus, setTranscriptionStatus] = useState<TranscriptionStatus>(TranscriptionStatus.Inactive);

  const mediaRecorderHookReturn = useMediaRecorder({
    onDataAvailable: enqueuePartialTranscription,
    sliceInterval: audioChunkSizeSeconds,
  });

  async function uploadAndEnqueuePartial(order: number) {
    const partial = transcriptionUploadQueue.current.get(order);

    // Exit if chunk doesn't exist, already completed, or exceeded retries
    if (!partial || partial.status === 'completed' || partial.retryCount >= maxUploadRetries) {
      return;
    }

    partial.status = 'uploading';

    try {
      const base64 = await blobToBase64(partial.blob);
      await transcriptionClient.queuePartialTranscription({
        audioBytes: base64,
        sessionId: sessionId.current,
        order,
        language: 'da',
      });

      // Success: mark completed and cleanup immediately
      partial.status = 'completed';
      transcriptionUploadQueue.current.delete(order);
    } catch (error) {
      partial.retryCount += 1;

      if (partial.retryCount >= maxUploadRetries) {
        // Max retries exceeded: mark as failed, keep in queue for diagnostics
        partial.status = 'failed';
      } else {
        // Retry after 2 second delay (matching current behavior)
        partial.status = 'pending';
        setTimeout(() => uploadAndEnqueuePartial(order), 2000);
      }
    }
  }

  function waitForUploadsToComplete(): Promise<void> {
    return new Promise(resolve => {
      const checkInterval = setInterval(() => {
        const partialUploads = Array.from(transcriptionUploadQueue.current.values());
        const hasActiveUploads = partialUploads.some(c => c.status === 'pending' || c.status === 'uploading');

        if (!hasActiveUploads) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100); // Check every 100ms
    });
  }

  function enqueuePartialTranscription(audioData: Blob): Promise<void> {
    // Assign order immediately when audio data arrives
    const order = nextOrder.current;
    nextOrder.current += 1;

    // Add to queue
    transcriptionUploadQueue.current.set(order, {
      blob: audioData,
      order,
      status: 'pending',
      retryCount: 0,
    });

    // Start upload - we don't wait for the result to allow queueing more audio immediately
    uploadAndEnqueuePartial(order);

    // Always resolve successfully - don't block MediaRecorder queue
    return Promise.resolve();
  }

  function stopPolling() {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    pollingStartTimeRef.current = null;
  }

  async function startPollingForTranscriptionStatus(): Promise<void> {
    if (pollingIntervalRef.current) return; // Already polling

    // Mark as polling
    setTranscriptionStatus(TranscriptionStatus.Processing);

    // Wait for any pending/uploading partials to complete
    await waitForUploadsToComplete();

    // Track polling start time
    pollingStartTimeRef.current = Date.now();

    async function pollTranscriptionStatus() {
      try {
        const response = await transcriptionClient.getTranscriptionStatus({
          sessionId: sessionId.current,
        });

        // Check for timeout and status is still pending
        if (response.overallStatus === 'pending') {
          const elapsedTime = Date.now() - (pollingStartTimeRef.current || 0);
          if (elapsedTime >= pollingTimeoutMs) {
            setTranscriptionStatus(TranscriptionStatus.ProcessingTimedOut);
            stopPolling();
            return;
          }
        }

        // Handle completed status - update transcription
        if (response.overallStatus === 'completed') {
          setTranscriptionStatus(TranscriptionStatus.Completed);
          stopPolling();
        } else if (response.overallStatus === 'failed') {
          setTranscriptionStatus(TranscriptionStatus.Failed);
          stopPolling();
        }
      } catch (error) {
        setTranscriptionStatus(TranscriptionStatus.Failed);
        stopPolling();
      }
    }

    // Initial fetch
    pollTranscriptionStatus();

    // Poll every 2 seconds
    pollingIntervalRef.current = setInterval(pollTranscriptionStatus, 2000);
  }

  // Cleanup polling on unmount
  useEffect(
    () => () => {
      stopPolling();
    },
    []
  );

  function reset() {
    // Stop polling if active
    stopPolling();

    // Clear upload queue
    transcriptionUploadQueue.current.clear();

    // Reset order counter
    nextOrder.current = 0;

    // Generate new session ID for next recording
    sessionId.current = uuidv4();

    // Clear isPolling state
    setTranscriptionStatus(TranscriptionStatus.Inactive);

    // Call original abort from useMediaRecorder
    mediaRecorderHookReturn.abortRecording();
  }

  async function wrappedStartRecording(includeSystemAudio: boolean, audioInputDeviceId: string | undefined) {
    await mediaRecorderHookReturn.startRecording(includeSystemAudio, audioInputDeviceId);
    setTranscriptionStatus(TranscriptionStatus.Active);
  }

  function wrappedStopRecording() {
    mediaRecorderHookReturn.stopRecording();
    if (transcriptionStatus === TranscriptionStatus.Active) {
      setTranscriptionStatus(TranscriptionStatus.Paused);
    }
  }

  return {
    startPollingForTranscription: startPollingForTranscriptionStatus,
    errorMessage: mediaRecorderHookReturn.errorMessage,
    startRecording: wrappedStartRecording,
    stopRecording: wrappedStopRecording,
    micInputAnalyser: mediaRecorderHookReturn.micInputAnalyser,
    systemAudioAnalyser: mediaRecorderHookReturn.systemAudioAnalyser,
    recordingStartTime: mediaRecorderHookReturn.recordingStartTime,
    abortRecording: reset,
    transcriptionStatus,
    sessionId: sessionId.current,
  };
}

interface PartialTranscriptionUploadState {
  blob: Blob;
  order: number;
  status: 'pending' | 'uploading' | 'completed' | 'failed';
  retryCount: number;
}

export { useTranscription };
